import { useCallback, useEffect, useState } from 'react';
import type { NotificationSettingsView, NotificationView } from '@galaxis/shared';
import { NOTIFICATION_KINDS, NOTIFICATION_KIND_LABELS } from '@galaxis/shared';
import {
  fetchNotifications, markNotificationsRead, fetchNotificationSettings, saveNotificationSettings,
} from '../../api/social.ts';
import { enablePush, disablePush, pushSupport } from '../../push/push-client.ts';
import { useGameStore } from '../../store/game-store.ts';

export function NotificationsPanel() {
  const [settings, setSettings] = useState<NotificationSettingsView | null>(null);
  const [inbox, setInbox] = useState<{ items: NotificationView[]; unread: number } | null>(null);
  const [webhook, setWebhook] = useState('');
  const [busy, setBusy] = useState(false);
  const pushToast = useGameStore(s => s.pushToast);

  const load = useCallback(() => {
    fetchNotificationSettings().then(setSettings).catch(() => undefined);
    fetchNotifications().then(setInbox).catch(() => undefined);
  }, []);
  useEffect(load, [load]);

  const save = async (patch: Parameters<typeof saveNotificationSettings>[0], ok = 'Mentve') => {
    setBusy(true);
    try { setSettings(await saveNotificationSettings(patch)); pushToast(ok); }
    catch (e) { pushToast(e instanceof Error ? e.message : 'A mentés nem sikerült', 'error'); }
    finally { setBusy(false); }
  };

  const togglePush = async (on: boolean) => {
    if (!settings) return;
    setBusy(true);
    try {
      if (on) {
        if (!settings.vapidPublicKey) { pushToast('A szerveren nincs beállítva a push-értesítés.', 'error'); return; }
        const r = await enablePush(settings.vapidPublicKey);
        if (r === 'denied') { pushToast('A böngésző letiltotta az értesítéseket.', 'error'); return; }
        if (r === 'unsupported') { pushToast('Ez a böngésző nem támogatja a push-értesítést.', 'error'); return; }
        setSettings(await saveNotificationSettings({ pushEnabled: true }));
        pushToast('Push-értesítés bekapcsolva ezen az eszközön');
      } else {
        await disablePush();
        setSettings(await saveNotificationSettings({ pushEnabled: false }));
      }
    } catch (e) { pushToast(e instanceof Error ? e.message : 'A beállítás nem sikerült', 'error'); }
    finally { setBusy(false); }
  };

  if (!settings) return <p className="text-sm text-slate-500">Beállítások betöltése…</p>;
  const support = pushSupport();

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      <section className="sci-fi-panel rounded-xl p-4" aria-label="Értesítési csatornák">
        <h2 className="text-sm font-semibold text-slate-200 mb-3">Csatornák</h2>
        <label className="flex items-center gap-3 min-h-[44px] text-sm text-slate-200">
          <input type="checkbox" className="w-5 h-5" checked={settings.pushEnabled} disabled={busy || !settings.vapidPublicKey || support === 'unsupported'}
            onChange={e => void togglePush(e.target.checked)} aria-label="Push-értesítés" />
          <span>
            Push-értesítés ezen az eszközön
            <span className="block text-xs text-slate-500">
              {!settings.vapidPublicKey ? 'A szerver még nincs felkészítve push-értesítésre.'
                : support === 'unsupported' ? 'Ez a böngésző nem támogatja.'
                : support === 'denied' ? 'A böngésző letiltotta; engedélyezd a webhely beállításaiban.'
                : `${settings.pushSubscriptions} feliratkozott eszköz`}
            </span>
          </span>
        </label>
        <div className="mt-3 border-t border-slate-700/50 pt-3">
          <label className="flex items-center gap-3 min-h-[44px] text-sm text-slate-200">
            <input type="checkbox" className="w-5 h-5" checked={settings.discordEnabled} disabled={busy || !settings.discordConfigured}
              onChange={e => void save({ discordEnabled: e.target.checked })} aria-label="Discord-értesítés" />
            <span>Discord-értesítés<span className="block text-xs text-slate-500">{settings.discordConfigured ? 'A webhook be van állítva (a cím titok, nem jelenik meg).' : 'Add meg a saját Discord-webhookodat.'}</span></span>
          </label>
          <form className="mt-2 flex flex-wrap gap-2" onSubmit={e => { e.preventDefault(); void save({ discordWebhookUrl: webhook.trim(), discordEnabled: webhook.trim() !== '' }, webhook.trim() ? 'Webhook mentve' : 'Webhook törölve').then(() => setWebhook('')); }}>
            <input className="flex-1 min-w-[200px] bg-slate-900 border border-slate-600 rounded px-2 py-2 text-slate-100 text-sm" placeholder="https://discord.com/api/webhooks/…" value={webhook} onChange={e => setWebhook(e.target.value)} aria-label="Saját Discord webhook cím" autoComplete="off" />
            <button type="submit" className="btn-primary min-h-[44px]" disabled={busy || (webhook.trim() === '' && !settings.discordConfigured)}>{webhook.trim() ? 'Mentés' : 'Törlés'}</button>
          </form>
        </div>
      </section>

      <section className="sci-fi-panel rounded-xl p-4" aria-label="Eseménytípusok">
        <h2 className="text-sm font-semibold text-slate-200 mb-2">Mikor értesítsünk?</h2>
        <table className="w-full text-sm">
          <thead><tr className="text-xs text-slate-500 text-left"><th className="font-normal py-1">Esemény</th><th className="font-normal w-16 text-center">Push</th><th className="font-normal w-20 text-center">Discord</th></tr></thead>
          <tbody>
            {NOTIFICATION_KINDS.map(k => (
              <tr key={k} className="border-t border-slate-800">
                <td className="py-2 text-slate-300">{NOTIFICATION_KIND_LABELS[k]}</td>
                {(['push', 'discord'] as const).map(ch => (
                  <td key={ch} className="text-center">
                    <input type="checkbox" className="w-5 h-5" checked={settings.kinds[k][ch]} disabled={busy}
                      aria-label={`${NOTIFICATION_KIND_LABELS[k]} – ${ch === 'push' ? 'push' : 'Discord'}`}
                      onChange={e => void save({ kinds: { [k]: { [ch]: e.target.checked } } })} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section aria-label="Beérkező értesítések" data-testid="inbox">
        <div className="flex justify-between items-baseline mb-2">
          <h2 className="text-sm font-semibold text-slate-300">Legutóbbi értesítések {inbox && inbox.unread > 0 && <span className="text-amber-300">({inbox.unread} új)</span>}</h2>
          {inbox && inbox.unread > 0 && <button type="button" className="nav-btn text-xs" onClick={() => void markNotificationsRead().then(load)}>Mind olvasott</button>}
        </div>
        {!inbox || inbox.items.length === 0 ? <p className="text-sm text-slate-500">Még nincs értesítésed.</p> : (
          <ul className="space-y-1.5">
            {inbox.items.map(n => (
              <li key={n.id} className={`rounded-lg px-3 py-2 text-sm border ${n.readAt ? 'border-slate-800 text-slate-400' : 'border-accent/40 text-slate-200'}`}>
                <div className="flex justify-between gap-2"><b className="font-medium">{n.title}</b><span className="text-[11px] text-slate-500">{new Date(n.createdAt).toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' })}</span></div>
                <div className="text-xs">{n.body}</div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
